import type { TableResponse } from "./floorApi";

/**
 * Shared floor geometry: lay a venue's drawn tables out zone-by-zone in stacked, labelled bands.
 *
 * This is the ONE spatial layout used by every read-only floor surface — the reservation dashboard's
 * `FloorView` (peek/planning) and the live-service `FloorMap` (operational, per-table state) — so both
 * render the manager's room identically and only differ in how a table is coloured/tapped. Kept pure
 * (no React, no tokens) and unit-tested in `../../bookings/views/floorLayout.test.ts`.
 */

/** The rendered edge of a table square, in scene units (matches the builder's grid feel). */
export const TABLE_SIZE = 44;

/**
 * The CSS px each scene unit renders at, CLAMPED so a `TABLE_SIZE` table stays in a comfortable tap
 * range (`minTablePx`..`maxTablePx`, ~44..64) regardless of viewport. Below the min the scene simply
 * overflows its pan container (scroll) instead of shrinking tap targets; above the max it stops
 * ballooning and centres. An unmeasured container (width 0, e.g. first paint / jsdom) clamps to the
 * min, so tables are always at least `minTablePx`.
 */
export function scenePxPerUnit(
  containerWidth: number,
  sceneWidth: number,
  minTablePx = 44,
  maxTablePx = 64,
): number {
  const raw = sceneWidth > 0 ? containerWidth / sceneWidth : 0;
  const min = minTablePx / TABLE_SIZE;
  const max = maxTablePx / TABLE_SIZE;
  return Math.min(max, Math.max(min, raw));
}

const ZONE_LABEL_H = 26;
const ZONE_PAD = 16;
const ZONE_GAP = 20;

export interface ZoneLike {
  id: string;
  name: string;
}
export interface ZoneBand {
  id: string;
  name: string;
  left: number;
  top: number;
  width: number;
  height: number;
}
export interface PlacedTable {
  tb: TableResponse;
  x: number;
  y: number;
}
export interface ZoneLayout {
  width: number;
  height: number;
  bands: ZoneBand[];
  placed: PlacedTable[];
}

/**
 * Lay tables out zone-by-zone in stacked horizontal bands (one per zone, in the plan's zone order,
 * then any unzoned tables last). Each band preserves its tables' relative geometry but is shifted down
 * by the cumulative height of the bands above it — so zones whose raw seed coordinates overlap render
 * as separate, labelled rooms instead of interleaved grids. Returns a scene width/height (for the
 * aspect-ratio box) plus the band rectangles and absolutely-placed tables, all in scene pixels.
 */
export function layoutZones(
  zones: readonly ZoneLike[],
  tables: readonly TableResponse[],
): ZoneLayout {
  const grouped = new Map<string, TableResponse[]>();
  const zoneIds = new Set(zones.map((z) => z.id));
  for (const tb of tables) {
    const key = zoneIds.has(tb.zoneId) ? tb.zoneId : "__other__";
    const list = grouped.get(key) ?? [];
    list.push(tb);
    grouped.set(key, list);
  }

  const order: ZoneLike[] = [
    ...zones.filter((z) => grouped.has(z.id)),
    ...(grouped.has("__other__") ? [{ id: "__other__", name: "" }] : []),
  ];

  const bands: ZoneBand[] = [];
  const placed: PlacedTable[] = [];
  let yCursor = 0;
  let maxWidth = 1;

  for (const zone of order) {
    const zt = grouped.get(zone.id) ?? [];
    if (zt.length === 0) continue;
    const minX = Math.min(...zt.map((tb) => tb.x));
    const minY = Math.min(...zt.map((tb) => tb.y));
    const contentW = Math.max(...zt.map((tb) => tb.x + TABLE_SIZE)) - minX;
    const contentH = Math.max(...zt.map((tb) => tb.y + TABLE_SIZE)) - minY;
    const contentTop = yCursor + ZONE_LABEL_H;

    for (const tb of zt) {
      placed.push({ tb, x: ZONE_PAD + (tb.x - minX), y: contentTop + (tb.y - minY) });
    }

    const bandHeight = ZONE_LABEL_H + contentH + ZONE_PAD;
    const bandWidth = contentW + ZONE_PAD * 2;
    bands.push({
      id: zone.id,
      name: zone.name,
      left: 0,
      top: yCursor,
      width: bandWidth,
      height: bandHeight,
    });
    maxWidth = Math.max(maxWidth, bandWidth);
    yCursor += bandHeight + ZONE_GAP;
  }

  const height = Math.max(1, yCursor - ZONE_GAP);
  // Bands span the full scene width so the labelled room reads as a lane.
  for (const band of bands) band.width = maxWidth;

  return { width: maxWidth, height, bands, placed };
}
